import { memo, useMemo } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { BreadcrumbNav, Button, Tooltip } from '@aimsos/ui';

import { FolderPlus, HardDrive, Upload } from 'lucide-react';

/**
 * The top of the folder in view (CT-5717), as in the prototype: the drive icon,
 * then the title — the drive's name at the root, its PATH ("<drive> › <folder>")
 * inside a sub-folder — and who owns it (the root) or what files there (a
 * folder); New folder for whoever may manage the drive, and Upload, drawn but
 * not designed yet: disabled, with a tooltip saying so.
 */
const DriveHeader = memo(function DriveHeader({ driveName, folder, breadcrumb, canManage, onNavigate, onNewFolder }) {
  const { t } = useTranslation('unified-profiles');

  const crumbs = useMemo(() => breadcrumb.map((crumb) => ({ label: crumb.name, path: crumb.id })), [breadcrumb]);

  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="flex min-w-0 items-start gap-3">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-md border border-edge text-content-secondary">
          <HardDrive size={18} aria-hidden />
        </span>
        <div className="flex min-w-0 flex-col gap-1">
          {folder ? (
            <>
              {/* The path IS the title; the heading stays for screen readers. */}
              <h2 className="sr-only">{folder.name}</h2>
              <div data-testid="drive-breadcrumb">
                <BreadcrumbNav items={crumbs} onNavigate={onNavigate} />
              </div>
            </>
          ) : (
            <h2 className="text-lg font-semibold text-content">{driveName}</h2>
          )}
          <p className="text-sm text-content-secondary">
            {folder ? (folder.sourceLine ?? '') : t('knowledge.drive.ownedBy')}
          </p>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {canManage ? (
          <Button variant="secondary" size="sm" leftIcon={FolderPlus} onClick={onNewFolder}>
            {t('knowledge.drive.newFolder.open')}
          </Button>
        ) : null}
        <Tooltip content={t('knowledge.drive.uploadUnavailable')} size="compact">
          <Button variant="secondary" size="sm" leftIcon={Upload} disabled>
            {t('knowledge.drive.upload')}
          </Button>
        </Tooltip>
      </div>
    </div>
  );
});

export default DriveHeader;
